import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TestModeService } from '../../../services/test-mode.service';
import { FieldConfig } from '../../../types/entity-definition.interface';
import {
  CesHourpickerFieldDebugPanelComponent,
  HourpickerDebugPanelStateChange,
} from '../ces-debug-panels/ces-hourpicker-field-debug-panel.component';
import { CesValidationErrorsComponent } from '../ces-validation-errors/ces-validation-errors.component';
import { CesHourpickerComponent } from '../inputs/ces-hourpicker.component';

export interface HourValidationError {
  message: string;
  condition: (value: string | null) => boolean;
}

@Component({
  selector: 'ces-hourpicker-field',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    FloatLabelModule,
    CesHourpickerComponent,
    CesValidationErrorsComponent,
    CesHourpickerFieldDebugPanelComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CesHourpickerFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CesHourpickerFieldComponent),
      multi: true,
    },
  ],
  templateUrl: './ces-hourpicker-field.component.html',
  styleUrl: './ces-hourpicker-field.component.scss',
})
export class CesHourpickerFieldComponent implements ControlValueAccessor, Validator, AfterViewInit, OnInit, OnChanges {
  @ViewChild('hourpickerElement') hourpickerElement!: CesHourpickerComponent;

  private cdRef = inject(ChangeDetectorRef);
  protected t = inject(TestModeService);
  private translateService = inject(TranslocoService);

  @Input() id: string = crypto.randomUUID();
  @Input() label?: string;
  @Input() supLabel?: string;
  @Input() class: string = '';
  @Input() labelClass: string = '';
  @Input() boldLabel?: boolean;
  @Input() disabled?: boolean;
  @Input() floatLabel?: boolean;
  @Input() showSeconds?: boolean;
  @Input() config?: FieldConfig;

  @Input() minTime?: string;
  @Input() maxTime?: string;

  @Input() required?: boolean;
  @Input() externalErrors?: Record<string, string>;
  // Sentinel: any ref change triggers ngOnChanges-driven revalidation.
  @Input() validateTrigger: unknown;

  @Output() valueChange = new EventEmitter<string | null>();
  @Output() focusEvent = new EventEmitter<void>();
  @Output() blurEvent = new EventEmitter<void>();
  @Output() timeChange = new EventEmitter<string | null>();
  @Output() validationComplete = new EventEmitter<{ valid: boolean; value: string | null }>();

  value: string | null = null;
  errors: Record<string, boolean> = {};
  touched = false;
  dirty = false;

  get untouched(): boolean {
    return !this.touched;
  }

  protected formControl?: AbstractControl;

  validations: Record<string, HourValidationError> = {};

  get effectiveLabel(): string {
    if (this.label) return this.label;
    if (this.config?.labelKey) return this.translateService.translate(this.config.labelKey);
    return this.config?.label ?? '';
  }

  get effectiveRequired(): boolean {
    return this.required ?? this.config?.required ?? false;
  }

  get effectiveDisabled(): boolean {
    return this.disabled ?? false;
  }

  get effectiveShowSeconds(): boolean {
    return this.showSeconds ?? false;
  }

  get hasErrors(): boolean {
    return Object.keys(this.errors).length > 0;
  }

  get pristine(): boolean {
    return this.formControl ? this.formControl.pristine : !this.dirty;
  }

  get valid(): boolean {
    return this.formControl ? this.formControl.valid : !this.hasErrors;
  }

  get invalid(): boolean {
    return this.formControl ? this.formControl.invalid : this.hasErrors;
  }

  get activeErrorMessages(): string[] {
    return Object.keys(this.errors)
      .filter(key => this.errors[key] && this.validations[key])
      .map(key => this.validations[key]?.message ?? '');
  }

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.setupValidations();
    this.validateConfiguration();

    if (this.config?.defaultValue !== undefined && (!this.value || this.value === '')) {
      const defaultValue = this.config.defaultValue;
      this.value = typeof defaultValue === 'string' ? defaultValue : null;
      this.onChange(this.value);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['validateTrigger'] && !changes['validateTrigger'].firstChange) {
      setTimeout(() => this.performValidation(true));
    } else if (changes['externalErrors'] && !changes['externalErrors'].firstChange) {
      this.performValidation();
    } else if (changes['minTime'] || changes['maxTime']) {
      this.setupValidations();
      this.performValidation();
    }
  }

  private setupValidations(): void {
    this.validations = {
      required: {
        message: this.translateService.translate('validation.required'),
        condition: (value: string | null) => {
          return this.effectiveRequired && (!value || value.trim() === '');
        },
      },
      invalidFormat: {
        message: this.translateService.translate('validation.invalidTime'),
        condition: (value: string | null) => {
          if (!value) return false;
          const timeRegex = this.effectiveShowSeconds
            ? /^([0-1]?[0-9]|2[0-3]):([0-5]?[0-9]):([0-5]?[0-9])$/
            : /^([0-1]?[0-9]|2[0-3]):([0-5]?[0-9])$/;
          return !timeRegex.test(value);
        },
      },
      minTime: {
        message: this.translateService.translate('validation.timeAfter', { time: this.minTime }),
        condition: (value: string | null) => {
          if (!value || !this.minTime) return false;
          return this.compareTime(value, this.minTime) < 0;
        },
      },
      maxTime: {
        message: this.translateService.translate('validation.timeBefore', { time: this.maxTime }),
        condition: (value: string | null) => {
          if (!value || !this.maxTime) return false;
          return this.compareTime(value, this.maxTime) > 0;
        },
      },
    };
  }

  private compareTime(time1: string, time2: string): number {
    const parseTime = (timeStr: string) => {
      const parts = timeStr.split(':');
      const hours = parseInt(parts[0] ?? '0', 10);
      const minutes = parseInt(parts[1] ?? '0', 10);
      const seconds = parts[2] ? parseInt(parts[2], 10) : 0;
      return hours * 3600 + minutes * 60 + seconds;
    };

    const seconds1 = parseTime(time1);
    const seconds2 = parseTime(time2);

    if (seconds1 < seconds2) return -1;
    if (seconds1 > seconds2) return 1;
    return 0;
  }

  private performValidation(emitValidation: boolean = false): ValidationErrors | null {
    this.errors = {};

    Object.keys(this.validations).forEach(key => {
      if (this.validations[key]?.condition(this.value)) {
        this.errors[key] = true;
      }
    });

    if (this.externalErrors) {
      Object.keys(this.externalErrors).forEach(key => {
        this.errors[key] = true;
      });
    }

    const hasErrors = Object.keys(this.errors).length > 0;
    this.cdRef.markForCheck();

    if (emitValidation) {
      this.validationComplete.emit({
        valid: !hasErrors,
        value: this.value,
      });
    }

    if (this.formControl) {
      hasErrors ? this.formControl.setErrors(this.errors) : this.formControl.setErrors(null);
    }

    return hasErrors ? this.errors : null;
  }

  ngAfterViewInit(): void {
    this.cdRef.detectChanges();
    this.performValidation();
  }

  writeValue(value: unknown): void {
    this.value = this.coerceToTimeString(value);

    setTimeout(() => {
      if (this.hourpickerElement) {
        this.hourpickerElement.writeValue(this.value);
      }
      this.cdRef.markForCheck();
    });

    this.performValidation();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private coerceToTimeString(value: unknown): string | null {
    if (value === null || value === undefined || value === '') return null;
    return typeof value === 'string' ? value : String(value);
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.touched = true;
      this.onTouched();
      this.performValidation();
      this.cdRef.markForCheck();
    }
  }

  markAsPristine(): void {
    if (this.formControl) {
      this.formControl.markAsPristine();
    }
    this.dirty = false;
  }

  markAsDirty(): void {
    if (this.formControl) {
      this.formControl.markAsDirty();
    }
    this.dirty = true;
  }

  markAsUntouched(): void {
    if (this.formControl) {
      this.formControl.markAsUntouched();
    }
    this.touched = false;
  }

  disable(): void {
    this.disabled = true;
  }

  enable(): void {
    this.disabled = false;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdRef.markForCheck();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    this.formControl = control;
    this.value = this.coerceToTimeString(control.value);

    if (control.touched && !this.touched) {
      this.touched = true;
      this.cdRef.markForCheck();
    }

    return this.performValidation();
  }

  onTimeChange(time: string | null): void {
    this.value = time;
    this.dirty = true;

    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.timeChange.emit(this.value);

    this.performValidation();
    this.cdRef.markForCheck();
  }

  onBlur(): void {
    this.touched = true;
    this.onTouched();
    this.blurEvent.emit();

    this.performValidation();
    this.cdRef.markForCheck();
  }

  onFocus(): void {
    this.focusEvent.emit();
  }

  private validateConfiguration(): void {
    if (this.floatLabel && !this.effectiveLabel) {
      console.warn(this.translateService.translate('common.warnings.hourpickerFloatLabelNoLabel'));
    }

    if (this.minTime && this.maxTime) {
      if (this.compareTime(this.minTime, this.maxTime) >= 0) {
        console.warn(this.translateService.translate('common.warnings.hourpickerMinTimeGreaterThanMax'));
      }
    }
  }

  onStateChange(event: HourpickerDebugPanelStateChange) {
    switch (event.property) {
      case 'pristine':
        event.value ? this.markAsPristine() : this.markAsDirty();
        break;
      case 'dirty':
        event.value ? this.markAsDirty() : this.markAsPristine();
        break;
      case 'touched':
        event.value ? this.markAsTouched() : this.markAsUntouched();
        break;
      case 'disabled':
        event.value ? this.disable() : this.enable();
        break;
    }
  }

  onValueChange(value: string | null) {
    this.value = value;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.timeChange.emit(this.value);
  }
}
